<template>
 <BContainer class="container-pages" v-if="recipesData?.data?.recipes">
          <BRow>
              <BCol xl="3">
                  <div class="shadow rounded p-element-page bg-white mb-2">                   
                   <FilterRecipe :categories="categories"></FilterRecipe>                  
                  </div>
              </BCol>
              <BCol xl="5">
                <RecipeSearch />
                <div class="shadow rounded p-element-page bg-white" v-if="category">
                     <h1 class="h1-title">{{ category.name }} | Recepti</h1>
                   <hr>
                 </div>
              <DisplayRecipePage :recipes="recipesData?.data?.recipes?.data"
                        :isDesktop="$screen.showDesktopAds"
                        :devOrProd="$screen.isProduction"></DisplayRecipePage>

                <div class="mt-3">
                   <PaginateAddress :lastPage="recipesData?.data?.recipes?.last_page"/>
                </div>
              </BCol>
              <BCol xl="4">
                  <div class="container-a mt-2 mb-3"  v-if="$screen.showDesktopAds">
                   <AdsEventTopRight></AdsEventTopRight>
                  </div>
                  <TopRecipe :items="recipesData.data.topRecipes.data"> </TopRecipe>
              </BCol>
          </BRow>
      </BContainer>
</template>

<script setup>

import { BContainer, BCol, BRow } from "bootstrap-vue-next";

import AdsEventTopRight from "~/components/GoogleAdvertisements/AdsEventTopRight.vue"; 
import FilterRecipe from "~/components/cms/recipe/recipe/FilterRecipe"; 
import TopRecipe from "~/components/widgets/Recipe/TopRecipe.vue";
import RecipeSearch from '~/components/search/RecipeSearch.vue'
import PaginateAddress from '~/components/cms/address/Display/PaginateAddress.vue'
import DisplayRecipePage from "~/components/cms/recipe/recipe/display/DisplayRecipePage.vue";
import { useRecipeStore } from "~/stores/recipe/recipe";
import { useShareMetaStore } from "~/stores/shareMeta";
const shareMeta = useShareMetaStore();
const config = useRuntimeConfig()

const isProdMode = ref(config.public.isProdMode)

const device = useDevice()

const useStore = useRecipeStore();
const route = useRoute();
const { $srbTolat } = useNuxtApp()


const pageParam = computed(() => Number(route.params.page))
const categoryParam = computed(() => String(route.params.category))

const { data: recipesData, pending, error, refresh } = await useAsyncData(
  () => `recipes-pages-${categoryParam.value}-t${pageParam.value}`,
  () => useStore.getRecipeListing({ 
          category: categoryParam.value,
          page: pageParam.value }),
  {
    watch: [() => categoryParam.value, () => pageParam.value],
  }
)
const categories = computed(() => recipesData.value?.data?.categories?.data ?? [])

const categoryMap = computed(() => {
  const map = new Map()
  for (const c of categories.value) {
    if (c?.name_replace) map.set(c.name_replace, c)
  }
  return map
})

const category = computed(() => categoryMap.value.get(categoryParam.value) ?? null)

const siteUrl = 'https://pijace.com'

const headTitle = computed(() => {
  const c = category.value
  if (!c) return 'Recepti | Pijace'
  return `${c.name} | Recepti | Pijace`
})

const headDescription = computed(() => {
  const c = category.value
  // ako imaš dobar meta opis u bazi, koristi njega
  if (c?.description_meta) return c.description_meta

  // fallback (bolji opis)
  return `Recepti iz kategorije ${c?.name ?? ''} – ideje za ručak, večeru i domaće specijalitete. Sastojci, priprema i saveti na Pijace.com.`
})

const headKeywords = computed(() => {
  const c = category.value?.name
  const base = [
    'recepti',
    'kuvanje',
    'domaći recepti',
    'recept',
    'pijace',
    'pijace.com',
    'hrana',
    'ideje za ručak',
    'ideje za večeru',
  ]

  if (c) base.unshift(`recepti ${c}`, c)
  return base.join(', ')
})

const canonicalUrl = computed(() => `${siteUrl}${route.fullPath}`)

const ogImage = computed(() => {
  const img = category.value?.img
  if (!img) return `${siteUrl}/api/img/recipe/default.webp` // stavi tvoj default
  // ako img već dolazi kao full url ostavi ga
  if (String(img).startsWith('http')) return img
  // ako je relativna putanja, spoji
  return `${siteUrl}${img.startsWith('/') ? '' : '/'}${img}`
})

// Apsolutni URL za pojedinačni recept (isti pattern kao u composables/useRecipeLink.ts)
// VAŽNO: single recipe ruta je /recept/ (singular), ne /recepti/
const buildRecipeUrl = (r) => {
  if (!r?.category_name && !category.value?.name) return null
  if (!r?.title || !r?.id) return null
  const catName = r.category_name ?? category.value.name
  const cat = $srbTolat.srbTolatPathSlugify(String(catName))
  const title = r.slug || $srbTolat.srbTolatPathSlugify(String(r.title))
  return `${siteUrl}/recept/${cat}/${title}/${r.id}`
}

// JSON-LD: CollectionPage + ItemList sa receptima u kategoriji (paginirano)
const collectionLd = computed(() => {
  const c = category.value
  const list = recipesData.value?.data?.recipes?.data ?? []
  const perPage = Number(recipesData.value?.data?.recipes?.per_page) || list.length || 0

  // SSR-safe: ako nema kategorije još, ne vraćaj ništa
  if (!c) return null

  // Apsolutna pozicija u celom listingu (za paginaciju)
  const pageOffset = (pageParam.value - 1) * perPage

  const itemListElement = list.slice(0, 10).map((r, i) => {
    const url = buildRecipeUrl(r)
    return {
      '@type': 'ListItem',
      position: pageOffset + i + 1,
      item: {
        '@type': 'Recipe',
        name: r?.title || `Recept ${i + 1}`,
        ...(r?.img ? { image: r.img } : {}),
        recipeCategory: c.name,
        ...(url ? { url } : {})
      }
    }
  })

  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': `${canonicalUrl.value}#collection`,
    name: headTitle.value,
    description: headDescription.value,
    url: canonicalUrl.value,
    image: [ogImage.value],
    inLanguage: 'sr',
    isPartOf: {
      '@type': 'WebSite',
      name: 'Pijace.com',
      url: siteUrl
    },
    publisher: {
      '@type': 'Organization',
      name: 'Pijace.com',
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/images/pijace-60.png`
      }
    },
    ...(itemListElement.length > 0
      ? {
          mainEntity: {
            '@type': 'ItemList',
            name: `Recepti - ${c.name}`,
            numberOfItems: list.length,
            itemListElement
          }
        }
      : {})
  }
})

// JSON-LD: BreadcrumbList — Pijace.com → Recepti → kategorija
const breadcrumbLd = computed(() => {
  if (!category.value) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Pijace.com',
        item: siteUrl
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Recepti',
        item: `${siteUrl}/recepti/1`
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: category.value.name,
        item: canonicalUrl.value
      }
    ]
  }
})

watchEffect(() => {
  if (!category.value) return
  shareMeta.setMeta({
    title: headTitle.value,
    description: headDescription.value,
    url: canonicalUrl.value,
  })
})

usePijaceSeo({
  title: headTitle,
  description: headDescription,
  url: canonicalUrl,
  type: 'website',
  image: ogImage,
  jsonLd: () => [collectionLd.value, breadcrumbLd.value],
})

// keywords + robots
useHead(() => ({
  meta: [
    { name: 'keywords', content: headKeywords.value },
    {
      name: 'robots',
      content: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
    },
  ],
}))

</script>